<script setup lang="ts">
import { nextTick, onMounted, ref, shallowRef, watch } from 'vue'
import type { PaginationQueryParams } from '@/@vxapp/@Enums/PaginationField'
import { PaginationField } from '@/@vxapp/@Enums/PaginationField'
import { ResponseCode } from '@/@vxapp/@Enums/PHP/HTTP/ResponseCode'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { cloneReactiveData } from '@/@core/utils/cloneReactiveData'
import { useEmailAccountsStore } from '@/@vxapp/Emails/useEmailAccountsStore'
import { useNotificationsStore } from '@/@vxapp/useNotificationsStore'
import EmailAccountDialog from '@/components/Dialogs/EmailAccounts/EmailAccountDialog.vue'
import EmailAccountsDataTable from '@/components/Tables/EmailAccounts/EmailAccountsDataTable.vue'
import { DialogDisplayType } from '@/enums/dialog-display-type'
import { emailAccountLinkErrorMessage } from '@/utils/emailAccountSender'

type EmailAccountData = Record<string, unknown>
type DisplayType = (typeof DialogDisplayType)[keyof typeof DialogDisplayType]

definePage({
  meta: { ...PageMeta.EmailAccounts.List.Meta, pageTitle: PageMeta.EmailAccounts.List.Title },
})

const log = createLogger('EmailAccountsListPage')
const route = useRoute()
const router = useRouter()
const emailAccountStore = useEmailAccountsStore() as any
const notify = useNotificationsStore()
const accountsTable = ref<InstanceType<typeof EmailAccountsDataTable> | null>(null)
const accountData = ref<EmailAccountData | null>(null)
const displayType = shallowRef<DisplayType>(DialogDisplayType.CREATE)
const isDialogVisible = shallowRef(false)
const isRouteReady = shallowRef(false)
const searchQuery = ref('')
const resultCount = ref(0)
const defaults: PaginationQueryParams = { ...PaginationField.QueryParams, sort: '+id' }
const limitItems = [10, 25, 50, 100]

const sortItems = [
  { title: 'Oldest first', value: '+id' },
  { title: 'Newest first', value: '-id' },
]

function stringValue(value: unknown) {
  const normalized = Array.isArray(value) ? value[0] : value

  return typeof normalized === 'string' || typeof normalized === 'number'
    ? String(normalized)
    : undefined
}

function positiveInteger(value: unknown, fallback: number) {
  const parsed = Number.parseInt(stringValue(value) ?? '', 10)

  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

function normalize(source: Record<string, unknown>): PaginationQueryParams {
  const requestedLimit = positiveInteger(source.limit, defaults.limit)
  const sort = stringValue(source.sort)
  const search = stringValue(source.search)?.trim()

  return {
    ...defaults,
    page: positiveInteger(source.page, defaults.page),
    limit: limitItems.includes(requestedLimit) ? requestedLimit : defaults.limit,
    sort: sort === '-id' ? '-id' : '+id',
    search: search || undefined,
  }
}

function same(left: PaginationQueryParams, right: PaginationQueryParams) {
  return (
    left.page === right.page &&
    left.limit === right.limit &&
    left.sort === right.sort &&
    left.search === right.search
  )
}

function applyRoute() {
  const current = normalize(emailAccountStore.queryParams ?? defaults)
  const incoming = normalize(route.query as Record<string, unknown>)

  searchQuery.value = incoming.search ?? ''
  if (same(current, incoming)) return false
  emailAccountStore.queryParams = incoming

  return true
}

function routeQuery(source: Record<string, unknown>) {
  const query = normalize(source)

  return {
    ...(query.page !== defaults.page ? { page: String(query.page) } : {}),
    ...(query.limit !== defaults.limit ? { limit: String(query.limit) } : {}),
    ...(query.sort !== defaults.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
  }
}

async function fetchAccounts() {
  await accountsTable.value?.fetchEmailAccounts()
}

async function updateQuery(patch: Partial<PaginationQueryParams>) {
  const next = normalize({ ...emailAccountStore.queryParams, ...patch })

  emailAccountStore.queryParams = next

  const query = routeQuery({ ...next })

  if (JSON.stringify(route.query) !== JSON.stringify(query)) await router.replace({ query })
  await nextTick()
  await fetchAccounts()
}

async function handleSearch() {
  await updateQuery({ search: searchQuery.value.trim() || undefined, page: 1 })
}

function handleSearchInput(value: unknown) {
  searchQuery.value = String(value ?? '')
}

async function handleClear() {
  searchQuery.value = ''
  await updateQuery({ search: undefined, page: 1 })
}

async function handleSort(value: unknown) {
  await updateQuery({ sort: String(value), page: 1 })
}

async function handleLimit(value: unknown) {
  await updateQuery({ limit: Number(value), page: 1 })
}

async function handlePage(page: number) {
  await updateQuery({ page })
}

async function openAccount(accountGUID: string, nextDisplayType: DisplayType) {
  const { responseCode } = await emailAccountStore.fetch(accountGUID)

  if (responseCode !== ResponseCode.OK) {
    log.error('Failed to load email account', { accountGUID, responseCode })

    return
  }

  accountData.value = cloneReactiveData(emailAccountStore.emailAccount ?? {})
  displayType.value = nextDisplayType
  isDialogVisible.value = true
}

async function handleCreateAccount() {
  const { responseCode } = await emailAccountStore.fetchTemplate()

  if (responseCode !== ResponseCode.OK) return

  const nextAccountData = cloneReactiveData(emailAccountStore.template ?? {}) as EmailAccountData

  if (nextAccountData.password === '********') nextAccountData.password = ''
  accountData.value = nextAccountData
  displayType.value = DialogDisplayType.CREATE
  isDialogVisible.value = true
}

async function linkCreatedAccount(accountGUID: unknown) {
  if (typeof accountGUID !== 'string' || accountGUID === '') return

  try {
    await emailAccountStore.linkMicrosoft(accountGUID)
    notify.success(
      'Email account linked to Microsoft. A verification email was sent to your Microsoft mailbox.'
    )
  } catch (error) {
    log.warn('Created email account could not be linked to Microsoft', error)
    notify.error(
      `The email account was created but not linked. ${emailAccountLinkErrorMessage(error)}`
    )
  }
}

async function handleSave(payload: EmailAccountData) {
  const isCreate = displayType.value === DialogDisplayType.CREATE

  try {
    const response = isCreate
      ? await emailAccountStore.create(payload, false)
      : await emailAccountStore.update(payload, false)

    if (![ResponseCode.OK, ResponseCode.CREATED].includes(response.responseCode)) return
    if (isCreate) await linkCreatedAccount(response.emailAccount?.accountGUID)
    await fetchAccounts()
  } catch (error) {
    log.error('Failed to save email account', error)
    notify.error(emailAccountLinkErrorMessage(error, 'The email account could not be saved.'))
  }
}

async function handleLinkChanged(account: EmailAccountData) {
  accountData.value = cloneReactiveData(account)
  await fetchAccounts()
}

function handleLoaded(count: number) {
  resultCount.value = count
}

async function handleEdit(accountGUID: string) {
  await openAccount(accountGUID, DialogDisplayType.EDIT)
}

async function handleView(accountGUID: string) {
  await openAccount(accountGUID, DialogDisplayType.VIEW)
}

emailAccountStore.queryParams = { ...defaults }
applyRoute()
isRouteReady.value = true
onMounted(fetchAccounts)

watch(
  () => route.query,
  async () => {
    if (!isRouteReady.value || !applyRoute()) return
    await nextTick()
    await fetchAccounts()
  },
  { deep: true }
)
</script>

<template>
  <main
    class="email-accounts-workspace"
    data-testid="email-accounts-workspace"
  >
    <section class="email-accounts-workspace__hero">
      <span>Delivery infrastructure</span>
      <h1>Every email starts with trust.</h1>
      <p>Manage the sending identities that power your outbound communication.</p>
      <div>
        <VBtn @click="handleCreateAccount">Connect an account</VBtn>
        <code>{{ resultCount }} identities / ready</code>
      </div>
    </section>

    <VCard class="email-accounts-workspace__toolbar">
      <VCardText>
        <VForm
          class="email-accounts-workspace__search"
          role="search"
          @submit.prevent="handleSearch"
        >
          <!-- vortex-validation-ignore-element: server search is non-validation filter UI -->
          <AppTextField
            :model-value="searchQuery"
            aria-label="Search email accounts"
            clearable
            hide-details
            placeholder="Search sending identities"
            vortex-validation-ignore-element
            @click:clear="handleClear"
            @update:model-value="handleSearchInput"
          />
          <VBtn
            type="button"
            @click="handleSearch"
            >Search</VBtn
          >
        </VForm>
        <!-- vortex-validation-ignore-element: result sorting is non-validation UI -->
        <AppSelect
          :model-value="emailAccountStore.queryParams?.sort ?? defaults.sort"
          :items="sortItems"
          aria-label="Sort email accounts"
          hide-details
          label="Sort"
          vortex-validation-ignore-element
          @update:model-value="handleSort"
        />
        <!-- vortex-validation-ignore-element: page size is non-validation UI -->
        <AppSelect
          :model-value="emailAccountStore.queryParams?.limit ?? defaults.limit"
          :items="limitItems"
          aria-label="Email accounts per page"
          hide-details
          label="Per page"
          vortex-validation-ignore-element
          @update:model-value="handleLimit"
        />
      </VCardText>
    </VCard>

    <EmailAccountsDataTable
      ref="accountsTable"
      @edit="handleEdit"
      @loaded="handleLoaded"
      @page-change="handlePage"
      @view="handleView"
    />
  </main>

  <EmailAccountDialog
    v-if="accountData"
    v-model:is-dialog-visible="isDialogVisible"
    :display-type="displayType"
    :email-account-data="accountData"
    @create="handleSave"
    @update="handleSave"
    @link-changed="handleLinkChanged"
  />
</template>

<style scoped lang="scss">
.email-accounts-workspace {
  display: grid;
  min-inline-size: 0;
  gap: 1rem;
}

.email-accounts-workspace__hero {
  display: grid;
  min-block-size: 30rem;
  align-content: center;
  border: 1px solid rgba(214, 235, 253, 0.19);
  border-radius: 1.5rem;
  background: #000;
  color: #f0f0f0;
  padding: clamp(2rem, 7vw, 6rem);
}

.email-accounts-workspace__hero > span {
  color: #ff801f;
  font-size: 0.75rem;
  text-transform: uppercase;
}

.email-accounts-workspace__hero h1 {
  max-inline-size: 11ch;
  margin: 1rem 0;
  color: #f0f0f0;
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(3.5rem, 8vw, 6rem);
  font-weight: 400;
  letter-spacing: -0.04em;
  line-height: 1;
}

.email-accounts-workspace__hero p {
  color: #a1a4a5;
}

.email-accounts-workspace__hero > div {
  display: flex;
  align-items: center;
  margin-block-start: 1.5rem;
  gap: 1rem;
}

.email-accounts-workspace__hero :deep(.v-btn) {
  min-block-size: 2.75rem;
  border-radius: 9999px;
  background: #fff;
  color: #000;
}

.email-accounts-workspace__hero code {
  color: #a1a4a5;
  font-size: 0.75rem;
}

.email-accounts-workspace__toolbar {
  border: 1px solid rgba(214, 235, 253, 0.19);
  border-radius: 1rem;
  background: #000;
  box-shadow: none;
}

.email-accounts-workspace__toolbar :deep(.v-card-text) {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: minmax(18rem, 1fr) minmax(10rem, 0.25fr) minmax(8rem, 0.15fr);
}

.email-accounts-workspace__search {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: 1fr auto;
}

.email-accounts-workspace__toolbar :deep(.v-btn) {
  min-block-size: 2.75rem;
  border: 1px solid rgba(214, 235, 253, 0.19);
  border-radius: 9999px;
  color: #f0f0f0;
}

@media (max-width: 699px) {
  .email-accounts-workspace__hero {
    min-block-size: 26rem;
    padding: 1.5rem;
  }

  .email-accounts-workspace__hero > div {
    align-items: start;
    flex-direction: column;
  }

  .email-accounts-workspace__toolbar :deep(.v-card-text),
  .email-accounts-workspace__search {
    grid-template-columns: 1fr;
  }
}
</style>
